import { watch } from 'vue';
import { defineStore } from 'pinia'
import { useNotificationStore, useContextStore } from '@/store';
import API from '@/services/api';
import { LitigationMachine, getMachine } from "@upclaim/machines";

import {
  AddAttachmentInputPayload,
  RemovePassengersPayloadSchema,
  RetrieveClaimWithRelationsResponse,
  ExtractPassengersPayloadSchema,
  AddEventPayloadSchema,
} from "@/interfaces/data-contracts";
import type { State } from "xstate";

const useClaimStore = defineStore("ClaimStore", {
  state: () => {
    return {
      claim: null as any as RetrieveClaimWithRelationsResponse,
      machineState: null as any as State<{
        LEGAL_STEP: string;
      }, { type: string }>,
    }
  },
  actions: {
    // @TODO: Plugin interface
    async retrieveClaim(claimId: string) {
      const claim = await API.retrieveClaimWithAllRelations(claimId);
      this.claim = claim

      if (!claim) {
        useContextStore().isOrphanConversation = true;
        this.machineState = null as any;
      } else {
        const { litigationName, versionNumber } = claim?.machine;
        const machine: LitigationMachine = getMachine(litigationName, versionNumber).machine;
        if (machine) {
          const eventsInOrder: string[] = claim.claimEvents.map(claimEvent => claimEvent.eventType.displayName);
          // @ts-ignore
          this.machineState = machine.initialState;
          for (const event of eventsInOrder) {
            // @ts-ignore
            this.machineState = machine.transition(this.machineState, { type: event });
          }
        }
      }

      return claim;
    },

    async refreshClaim() {
      await this.retrieveClaim(this.claim.id);
      return this.claim;
    },

    async extractPassengers(claimId: string, payload: ExtractPassengersPayloadSchema) {
      const { usersIds } = payload;
      const { success } = await API.extractPassengers(claimId, payload);

      if (success && this.claim) {
        this.claim.usersClaims = this.claim.usersClaims.filter(userClaim => {
          return !usersIds.includes(userClaim.userId)
        });

        useNotificationStore().emitNotification({
          title: "Opération réalisée avec succès",
          type: "success",
        });
      }
      return usersIds;
    },

    async removePassengers(claimId: string, payload: RemovePassengersPayloadSchema) {
      const { usersIds } = payload;
      const { success } = await API.removePassengers(claimId, payload);

      if (success && this.claim) {
        this.claim.usersClaims.forEach(userClaim => {
          // @TODO abortionId returned from API
          if (usersIds.includes(userClaim.userId)) userClaim.abortionId = 1;
        });
      }
    },

    async addAttachment(claimId: string, attachment: AddAttachmentInputPayload) {
      const data = await API.addClaimAttachment(claimId, attachment);
      if (data && this.claim) this.claim.attachments = [...this.claim.attachments, data]
    },

    async removeAttachment(attachmentId: string) {
      const { success } = await API.removeClaimAttachment(attachmentId);
      if (success && this.claim) this.claim.attachments.filter((attachment) => attachment.id !== attachmentId);
    },

    async sendEvent(claimId: string, event: AddEventPayloadSchema) {
      const { success } = await API.addEvent(claimId, event);
      if (success) this.retrieveClaim(claimId);
    },
  }
});

export default useClaimStore;